import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  FaPlay, FaChevronDown, FaTimes, FaUserTie, FaCode, FaHandshake, FaFutbol, FaUser,
} from "react-icons/fa";
import me from "../assets/IMG_1267.jpg";

/* ---------- Profiles ---------- */
export const PROFILES = [
  { id: "aayam", name: "Aayam", icon: FaUser, photo: me, from: "#e50914", to: "#6b0b12",
    order: ["story", "moments", "skills", "projects", "journey", "experience", "certs", "memories"],
    note: "Welcome. The full series, in order.",
    tag: "The full series, in order" },
  { id: "recruiter", name: "Recruiter", icon: FaUserTie,   from: "#e50914", to: "#6b0b12",
    order: ["projects", "experience", "skills", "certs", "memories"],
    note: "Welcome, recruiter. Start with the projects, then experience and skills.",
    tag: "Projects, experience & skills first" },
  { id: "developer", name: "Developer", icon: FaCode,      from: "#0a7cff", to: "#0b2f6b",
    order: ["skills", "projects", "certs", "experience", "memories"],
    note: "Welcome, developer. The stack comes first, then the code behind each project.",
    tag: "Stack and code first" },
  { id: "client",    name: "Client",    icon: FaHandshake, from: "#1db954", to: "#0a4a25",
    order: ["projects", "experience", "certs", "skills", "memories"],
    note: "Welcome. Here's what I've built and delivered, and how to hire me.",
    tag: "What I've built, and how to hire me" },
  { id: "friend",    name: "Friend",    icon: FaFutbol,    from: "#f5a623", to: "#7a3d05",
    order: ["memories", "projects", "certs", "skills", "experience"],
    note: "Hey, friend. Expo photos and videos are up first.",
    tag: "Expo photos and videos first" },
];
export const grad = p => `linear-gradient(135deg, ${p.from}, ${p.to})`;

/* ---------- Intro sound: original synthesized "boom + swell + shimmer" (no audio file needed) ---------- */
export function createIntroSound() {
  const AC = typeof window !== "undefined" && (window.AudioContext || window.webkitAudioContext);
  if (!AC) return null;
  let ctx;
  try { ctx = new AC(); } catch { return null; }
  const t = ctx.currentTime + 0.05;

  /* master volume: raise or lower this number (0 to 1) */
  const master = ctx.createGain();
  master.gain.value = 0.55;
  const comp = ctx.createDynamicsCompressor();
  master.connect(comp);
  comp.connect(ctx.destination);

  /* a pitched, decaying drum-like hit */
  const hit = (at, f0, f1, len, peak) => {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(f0, at);
    o.frequency.exponentialRampToValueAtTime(f1, at + len * 0.85);
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(peak, at + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, at + len);
    o.connect(g); g.connect(master);
    o.start(at); o.stop(at + len + 0.05);
  };

  /* 1. two deep cinematic hits: a soft one, then the big one as the logo lands */
  hit(t, 85, 50, 0.5, 0.55);
  hit(t + 0.4, 105, 38, 1.9, 1.0);

  /* short punchy click on the big hit */
  const nb = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.18), ctx.sampleRate);
  const nd = nb.getChannelData(0);
  for (let i = 0; i < nd.length; i++) nd[i] = (Math.random() * 2 - 1) * (1 - i / nd.length);
  const ns = ctx.createBufferSource();
  ns.buffer = nb;
  const nf = ctx.createBiquadFilter();
  nf.type = "bandpass";
  nf.frequency.value = 900;
  nf.Q.value = 0.8;
  const ng = ctx.createGain();
  ng.gain.value = 0.18;
  ns.connect(nf); nf.connect(ng); ng.connect(master);
  ns.start(t + 0.4);

  /* 2. dark rising synth swell (A minor), swept by a low-pass filter */
  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.Q.value = 5;
  lp.frequency.setValueAtTime(200, t);
  lp.frequency.exponentialRampToValueAtTime(2800, t + 0.45);
  lp.frequency.exponentialRampToValueAtTime(350, t + 2.4);
  const sg = ctx.createGain();
  sg.gain.setValueAtTime(0.0001, t);
  sg.gain.exponentialRampToValueAtTime(0.26, t + 0.45);
  sg.gain.exponentialRampToValueAtTime(0.0001, t + 2.5);
  lp.connect(sg); sg.connect(master);
  [110, 164.81, 220, 261.63].forEach((f, i) => {
    const o = ctx.createOscillator();
    o.type = "sawtooth";
    o.frequency.value = f;
    o.detune.value = i % 2 ? 6 : -6;
    o.connect(lp);
    o.start(t); o.stop(t + 2.6);
  });

  /* 3. bright shimmer that blooms after the hit */
  [440, 659.25, 880, 1318.5].forEach((f, i) => {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.value = f;
    g.gain.setValueAtTime(0.0001, t + 0.4);
    g.gain.exponentialRampToValueAtTime(0.05 / (1 + i * 0.35), t + 0.55);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 2.6);
    o.connect(g); g.connect(master);
    o.start(t + 0.4); o.stop(t + 2.7);
  });

  const born = performance.now();
  const resume = () => { if (ctx.state === "suspended") ctx.resume().catch(() => {}); };
  resume(); /* works when the browser allows autoplay; otherwise the first tap/key resumes it */

  return {
    resume,
    stop(fast) {
      if (ctx.state === "closed") return;
      if (fast || performance.now() - born < 400) {
        try { master.gain.setTargetAtTime(0, ctx.currentTime, 0.03); } catch (e) { /* ignore */ }
        setTimeout(() => ctx.close().catch(() => {}), 160);
      } else {
        setTimeout(() => ctx.close().catch(() => {}), 3000); /* let the tail ring into the next screen */
      }
    },
  };
}

/* ---------- Intro (once per session) ---------- */
export function Intro({ onDone }) {
  const [out, setOut] = useState(false);
  const done = useRef(onDone);
  done.current = onDone;
  const skipped = useRef(false);

  useEffect(() => {
    const snd = createIntroSound();
    const wake = () => { if (snd && !skipped.current) snd.resume(); };
    const evs = ["pointerdown", "keydown", "touchstart"];
    evs.forEach(ev => window.addEventListener(ev, wake, { once: true }));
    const a = setTimeout(() => setOut(true), 1500);
    const b = setTimeout(() => done.current(), 1800);
    return () => {
      clearTimeout(a); clearTimeout(b);
      evs.forEach(ev => window.removeEventListener(ev, wake));
      if (snd) snd.stop(skipped.current);
    };
  }, []);

  const skip = () => { skipped.current = true; onDone(); };

  return (
    <div className={`nx-intro${out ? " out" : ""}`} role="button" tabIndex={0}
      aria-label="Skip intro" onClick={skip}
      onKeyDown={e => (e.key === "Enter" || e.key === " ") && skip()}>
      <span>AAYAM</span>
    </div>
  );
}

/* ---------- Who's watching ---------- */
export function ProfileGate({ onPick }) {
  const [sel, setSel] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const choose = p => {
    if (sel) return;
    const quick = typeof window !== "undefined" && window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (quick) { onPick(p); return; }
    setSel(p.id);
    timer.current = setTimeout(() => onPick(p), 560);
  };
  return (
    <div className={`nx-gate${sel ? " picked" : ""}`}>
      <span className="nx-gate-logo" aria-hidden="true">AAYAM</span>
      <h1>Who's watching?</h1>
      <div className="nx-gate-list">
        {PROFILES.map((p, i) => (
          <button key={p.id} className={`nx-prof${sel === p.id ? " sel" : ""}`}
            style={{ "--i": i, "--glow": p.from }} onClick={() => choose(p)}>
            <span className="nx-av" style={{ background: grad(p) }}>
              {p.photo ? <img src={p.photo} alt="" /> : <p.icon />}
            </span>
            <span className="nx-pn">{p.name}</span>
            {p.tag && <span className="nx-pt">{p.tag}</span>}
          </button>
        ))}
      </div>
      <p className="nx-gate-note">Every profile watches the same true story of Aayam. Your pick changes what plays first.</p>
      <button className="nx-skip" onClick={() => onPick(PROFILES[0])}>Skip to portfolio</button>
    </div>
  );
}

/* ---------- Scroll reveal: rows fade/slide in once as they enter the viewport ---------- */
export function useReveal() {
  const ref = useRef(null);
  const [seen, setSeen] = useState(() => typeof IntersectionObserver === "undefined");
  useEffect(() => {
    if (seen) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setSeen(true); io.disconnect(); }
    }, { threshold: 0.05, rootMargin: "0px 0px -6% 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [seen]);
  return [ref, seen ? "rv in" : "rv"];
}

/* ---------- Scrolling row ---------- */
export function Row({ title, id, to, note, children }) {
  const ref = useRef(null);
  const [rvRef, rv] = useReveal();
  const go = d => ref.current.scrollBy({ left: d * ref.current.clientWidth * 0.85, behavior: "smooth" });
  return (
    <section className={`nx-row ${rv}`} ref={rvRef} id={id} aria-label={title}>
      {to
        ? <div className="nx-rh"><h2>{title}</h2><Link className="nx-all" to={to}>Explore all <span aria-hidden="true">›</span></Link></div>
        : <h2>{title}</h2>}
      {note && <p className="nx-rnote">{note}</p>}
      <div className="nx-rw">
        <button className="nx-arr nx-l" aria-label="Scroll left" onClick={() => go(-1)}>‹</button>
        <div className="nx-track" ref={ref}>{children}</div>
        <button className="nx-arr nx-r" aria-label="Scroll right" onClick={() => go(1)}>›</button>
      </div>
    </section>
  );
}

/* ---------- Images with skeleton shimmer ---------- */
export function Img({ src, alt = "" }) {
  const [ok, setOk] = useState(false);
  return (
    <span className={`nx-img${ok ? " ok" : ""}`}>
      <img src={src} alt={alt} loading="lazy" onLoad={() => setOk(true)}
        ref={el => { if (el && el.complete) setOk(true); }} />
    </span>
  );
}

/* ---------- Project posters: real screenshot if present, else a designed gradient ---------- */
const shots = {};
try {
  const ctx = require.context("../assets/projects", false, /\.(png|jpe?g|webp)$/i);
  ctx.keys().forEach(k => { shots[k.replace(/^\.\/|\.\w+$/g, "")] = ctx(k); });
} catch (e) { /* no screenshots yet */ }

const SLUGS = { 1: "soluxe", 2: "study-sync", 3: "eventghar", 4: "kickoff", 5: "home-ground-effect", 6: "pet-alert" };
const PAL = [["#e50914", "#4a070c"], ["#0a7cff", "#0a2552"], ["#1db954", "#083d1e"], ["#f5a623", "#6b3504"], ["#9b5de5", "#2d1650"], ["#00bbf9", "#052f40"]];
const initials = t => t.split(/\s+/).filter(w => /^[A-Za-z]/.test(w) && !/^(the|a|an|of)$/i.test(w)).slice(0, 2).map(w => w[0]).join("").toUpperCase();

/* Projects whose image is a logo (not a 16:9 screenshot): shown whole, centred, on a matching background.
   KickOff and Home Ground Effect are full-bleed, so they keep the default cover crop. */
const LOOK = {
  "soluxe":     { bg: "#0e0e0e" },
  "study-sync": { bg: "#0f1b2d" },
  "eventghar":  { bg: "#161616" },
  "pet-alert":  { bg: "#eef5f8" },
};

export function Poster({ p }) {
  const slug = p.shot || SLUGS[p.id];
  const src = shots[slug];
  const look = src ? LOOK[slug] : null;
  const [ok, setOk] = useState(false);
  const [g1, g2] = PAL[(p.id - 1) % PAL.length];
  return (
    <span className={`nx-poster${look ? " logo" : ""}${src && !ok ? " load" : ""}`}
      style={look ? { "--g1": g1, "--g2": g2, background: look.bg } : { "--g1": g1, "--g2": g2 }}>
      {src
        ? <img src={src} alt="" loading="lazy" onLoad={() => setOk(true)} ref={el => { if (el && el.complete) setOk(true); }} />
        : <span className="nx-ini">{initials(p.title)}</span>}
    </span>
  );
}

/* ---------- Hover-expand project card ---------- */
export function ProjectCard({ p, onOpen }) {
  const done = p.status === "Completed";
  return (
    <div className="nx-hc">
      <div className="nx-hc-in">
        <button className="nx-hc-img" onClick={() => onOpen(p)} aria-label={`${p.title}, open details`}>
          <Poster p={p} />
          <span className="nx-cap"><b>{p.title}</b><small>{p.category}</small></span>
        </button>
        <div className="nx-hc-info">
          <div className="nx-ic">
            <a className="nx-round nx-round-w" href={p.github} target="_blank" rel="noopener noreferrer" aria-label={`View ${p.title} code`}><FaPlay /></a>
            <button className="nx-round" onClick={() => onOpen(p)} aria-label="More info"><FaChevronDown /></button>
          </div>
          <div className="nx-meta"><b className={done ? "nx-match" : "nx-wip"}>{p.status}</b><span className="nx-pill">{p.category}</span></div>
          <p className="nx-stk">{p.stack.slice(0, 4).join(" · ")}</p>
        </div>
      </div>
    </div>
  );
}

/* ---------- Accessible dialog: focus trap, Esc, restore focus, scroll lock ---------- */
export function Dialog({ onClose, label, className, scrollKey, children }) {
  const ref = useRef(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const prev = document.activeElement;
    const el = ref.current;
    const items = () => el.querySelectorAll('a[href],button:not([disabled]),video[controls]');
    (items()[0] || el).focus();
    const old = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const key = e => {
      if (e.key === "Escape") { close.current(); return; }
      if (e.key !== "Tab") return;
      const f = items();
      if (!f.length) { e.preventDefault(); return; }
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("keydown", key);
      document.body.style.overflow = old;
      if (prev && prev.focus) prev.focus();
    };
  }, []);

  useEffect(() => { if (ref.current) ref.current.scrollTop = 0; }, [scrollKey]);

  return createPortal(
    <div className="nx-modal" onMouseDown={e => { if (e.target === e.currentTarget) close.current(); }}>
      <div className={className} role="dialog" aria-modal="true" aria-label={label} tabIndex={-1} ref={ref}>
        {children}
      </div>
    </div>,
    document.body
  );
}

export function Lightbox({ item, onClose }) {
  return (
    <Dialog className="nx-lb" label={item.alt || "Preview"} onClose={onClose}>
      <button className="nx-x" onClick={onClose} aria-label="Close"><FaTimes /></button>
      {item.type === "video"
        ? <video src={item.src} controls autoPlay />
        : <img src={item.src} alt={item.alt || ""} />}
    </Dialog>
  );
}

export function ProjectModal({ p, all, onClose, onSelect }) {
  const others = all.filter(x => x.id !== p.id);
  const related = [...others.filter(x => x.category === p.category), ...others.filter(x => x.category !== p.category)].slice(0, 3);
  const done = p.status === "Completed";
  return (
    <Dialog className="nx-box" label={p.title} onClose={onClose} scrollKey={p.id}>
      <button className="nx-x" onClick={onClose} aria-label="Close"><FaTimes /></button>
      <div className="nx-mhead">
        <Poster p={p} />
        <div className="nx-mh-in">
          <h3>{p.title}</h3>
          <a className="nx-btn nx-btn-w" href={p.github} target="_blank" rel="noopener noreferrer"><FaPlay /> View code</a>
        </div>
      </div>
      <div className="nx-facts">
        <div><small>Status</small><b className={done ? "nx-match" : "nx-wip"}>{p.status}</b></div>
        <div><small>Category</small><b>{p.category}</b></div>
        <div><small>Technologies</small><b>{p.stack.length}</b></div>
      </div>
      <div className="nx-mbody">
        <div>
          <p>{p.long}</p>
        </div>
        <div>
          <p className="nx-k">Tech stack</p>
          <div className="nx-tags">{p.stack.map(t => <span key={t}>{t}</span>)}</div>
        </div>
      </div>
      <div className="nx-more">
        <h4>More like this</h4>
        <div className="nx-relgrid">
          {related.map(r => (
            <button key={r.id} className="nx-rel" onClick={() => onSelect(r)} aria-label={`Open ${r.title}`}>
              <Poster p={r} />
              <span className="nx-cap"><b>{r.title}</b><small>{r.category}</small></span>
            </button>
          ))}
        </div>
      </div>
    </Dialog>
  );
}
